// @ts-nocheck
import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, IconButton } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { get, patch } from "requests";
import { useSubmissionStore } from "../../../../models/applications/SubmissionStore";
import { useMerchantContext } from "./MerchantContext";
import { states, provinces, getStatesProvinces, getStateProvinceLabel, getZipPostalLabel, validateZipOrPostalCode, formatPostalCode } from "../../../../utils/States";
import { Grid } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { Logger } from "html2canvas/dist/types/core/logger";
import MDSnackbar from "components/MDSnackbar";

const MerchantBusinessInformation = () => {
  const { merchantBusinessInfo, storeData, setStoreData } = useSubmissionStore();
  const { clearData, selectedCountry } = useMerchantContext();
  const [isExpanded, setIsExpanded] = useState(true);
  const [openDropdown, setOpenDropdown] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [data, setData] = useState([
    {
      label: "Legal Entity Type",
      value: "",
      isDropdown: true,
      key: "legalEntity",
      options: [
        "Corporation",
        "LLC",
        "Partnership",
        "Ltd.Partnership",
        "LLP",
        "Sole Proprietorship",
      ],
      required: false,
      error: false,
      helperText: "",
    },
    {
      label: "State of Incorporation",
      value: "",
      isDropdown: true,
      key: "stateOfIncorp",
      required: false,
      error: false,
      helperText: "",
    },
    {
      label: "Email Address",
      value: "",
      isDropdown: false,
      key: "emailAddress",
      required: false,
      error: false,
      helperText: "",
      validate: (value) => {
        if (!value) return true;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value);
      },
    },
    {
      label: "Business Phone",
      value: "",
      isDropdown: false,
      key: "businessPhoneNumber",
      required: false,
      error: false,
      helperText: "",
      validate: (value) => {
        if (!value) return true;
        const digits = value.replace(/\D/g, "");
        return digits.length === 10;
      },
    },
    {
      label: "Business Fax",
      value: "",
      isDropdown: false,
      key: "businessFaxId1",
      required: false,
      error: false,
      helperText: "",
      validate: (value) => {
        if (!value) return true;
        const digits = value.replace(/\D/g, "");
        return digits.length === 10;
      },
    },
    {
      label: "Business Start Date",
      value: "",
      isDropdown: false,
      key: "businessStartDate",
      required: false,
      error: false,
      helperText: "",
      validate: (value) => {
        if (!value) return true;
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        return dateRegex.test(value);
      },
    },
    {
      label: "Business Website",
      value: "",
      isDropdown: false,
      key: "companyWebsite",
      required: false,
      error: false,
      helperText: "",
      validate: (value) => {
        if (!value) return true;
        const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
        return urlRegex.test(value);
      },
    },
    {
      label: "Physical Street Address",
      value: "",
      isDropdown: false,
      key: "physicalAddress.address",
      required: false,
      error: false,
      helperText: "",
    },
    {
      label: "Physical City",
      value: "",
      isDropdown: false,
      key: "physicalAddress.city",
      required: false,
      error: false,
      helperText: "",
    },
    {
      label: "Physical State",
      value: "",
      isDropdown: true,
      key: "physicalAddress.state",
      required: false,
      error: false,
      helperText: "",
    },
    {
      label: "Physical Zip Code",
      value: "",
      isDropdown: false,
      key: "physicalAddress.zipCode",
      required: false,
      error: false,
      helperText: "",
    },
    {
      label: "Mailing Street Address",
      value: "",
      isDropdown: false,
      key: "mailingAddress.address",
      required: false,
      error: false,
      helperText: "",
    },
    {
      label: "Mailing City",
      value: "",
      isDropdown: false,
      key: "mailingAddress.city",
      required: false,
      error: false,
      helperText: "",
    },
    {
      label: "Mailing State",
      value: "",
      isDropdown: true,
      key: "mailingAddress.state",
      required: false,
      error: false,
      helperText: "",
    },
    {
      label: "Mailing Zip Code",
      value: "",
      isDropdown: false,
      key: "mailingAddress.zipCode",
      required: false,
      error: false,
      helperText: "",
    },
  ]);

  const toggleExpand = () => setIsExpanded((prev) => !prev);

  useEffect(() => {
    if (clearData) {
      setData((prevData) =>
        prevData.map((item) => ({
          ...item,
          value: "",
          error: false,
          helperText: "",
        }))
      );
    }
  }, [clearData]);

  useEffect(() => {
    if (merchantBusinessInfo?.merchantId) {
      get(`fetchMerchantAndOwners/${merchantBusinessInfo.merchantId}`)
        .then((response) => {
          const newData = data.map((item) => {
            const value = getValueFromMerchantInfo(item.key, response);
            return {
              ...item,
              value: value !== undefined ? value : item.value,
              error: false,
              helperText: "",
            };
          });
          setData(newData);
          setStoreData(newData);
        })
        .catch(console.error);
    }
  }, [merchantBusinessInfo]);

  useEffect(() => {
    let timeoutId;
    if (snackbarOpen) {
      console.log("Snackbar opened, setting timeout for 3 seconds");
      timeoutId = setTimeout(() => {
        console.log("Auto-closing snackbar after 3 seconds");
        setSnackbarOpen(false);
        setErrorMessage("");
      }, 3000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [snackbarOpen]);

  const patchMerchant = async (index: number) => {
    const updateData = { [storeData[index].key]: storeData[index].value };
    
    // Add country_code to all address fields
    const addressFields = [
      'physicalAddress.address',
      'physicalAddress.city', 
      'physicalAddress.state', 
      'physicalAddress.zipCode',
      'mailingAddress.address',
      'mailingAddress.city',
      'mailingAddress.state', 
      'mailingAddress.zipCode'
    ];
    const isAddressField = addressFields.includes(storeData[index].key);
    
    if (isAddressField) {
      updateData.country_code = selectedCountry || "US"; // Default to US if selectedCountry is undefined
    }
    
    if (!merchantBusinessInfo.merchantId) {
      console.warn("merchant id not found");
      return;
    }
    await patch(`merchant/${merchantBusinessInfo.merchantId}`, updateData).catch((e) => {
      const error = e.response?.data?.error?.[0] || "An error occurred";
      setErrorMessage(error);
      setSnackbarOpen(true);
      console.log("Setting snackbar open with error:", error);
    });
  };

  const getValueFromMerchantInfo = (key, responseData) => {
    if (!responseData) return "";
    const keys = key.split(".");
    let value = responseData;
    for (const k of keys) {
      if (value && value.hasOwnProperty(k)) {
        value = value[k];
      } else {
        return "";
      }
    }
    return value === null || value === undefined ? "" : value;
  };

  const handleInputChange = (index, event) => {
    const newData = [...storeData];
    const field = newData[index];
    const previousValue = field.value;
    let value = event.target.value;

    if (field.key === "businessPhoneNumber" || field.key === "businessFaxId1") {
      value = formatPhoneNumber(value, previousValue);
    } else if (field.key.includes("zipCode")) {
      if (selectedCountry === "CA") {
        // Canadian postal code: allow letters, digits, and spaces, limit to 7 chars (A1A 1A1)
        value = value.replace(/[^A-Za-z0-9\s]/g, "").slice(0, 7);
      } else {
        // US zip code: only digits, limit to 5
        value = value.replace(/\D/g, "").slice(0, 5);
      }
    } else if (field.key === "businessStartDate") {
      value = formatDate(value);
    }

    field.value = value;

    // Special validation for zip/postal codes
    if (field.key.includes("zipCode")) {
      const isValid = validateZipOrPostalCode(value, selectedCountry);
      field.error = value && !isValid;
      field.helperText = field.error ? getErrorMessage(field) : "";
    } else if (field.validate) {
      field.error = !field.validate(value);
      field.helperText = field.error ? getErrorMessage(field) : "";
    } else if (field.required && !value.trim()) {
      field.error = true;
      field.helperText = "This field is required";
    } else {
      field.error = false;
      field.helperText = "";
    }

    setStoreData(newData);
  };

  const formatPhoneNumber = (value, previousValue) => {
    if (previousValue && value.length < previousValue.length) {
      return value.replace(/\D/g, "");
    }

    const digits = value.replace(/\D/g, "").slice(0, 10);

    if (digits.length <= 3) return digits;

    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const formatDate = (value) => {
    value = value.replace(/[^\d-]/g, "");
    if (value.length > 4 && value[4] !== "-") {
      value = value.slice(0, 4) + "-" + value.slice(4);
    }
    if (value.length > 7 && value[7] !== "-") {
      value = value.slice(0, 7) + "-" + value.slice(7);
    }
    return value.slice(0, 10);
  };

  const getErrorMessage = (field) => {
    if (field.key === "emailAddress") return "Invalid email format";
    if (field.key === "businessPhoneNumber") return "10 digits required";
    if (field.key === "businessFaxId1") return "10 digits required";
    if (field.key === "businessStartDate") return "YYYY-MM-DD format required";
    if (field.key.includes("zipCode")) {
      return selectedCountry === "CA"
        ? "Valid format: A1A 1A1"
        : "5 digit zip code required";
    }
    if (field.key === "companyWebsite") return "Invalid URL format";
    return "Invalid value";
  };

  const handleDropdownToggle = (label) => {
    setOpenDropdown((prev) => (prev === label ? "" : label));
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
        marginBottom: "12px",
        border: "1px solid #f0f0f0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Merchant Business Information
        </Typography>
        <IconButton onClick={toggleExpand}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {isExpanded && (
        <form>
          <Grid container spacing={3} paddingX={2}>
            {data.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} lg={2} key={index}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    color: "#6b7280",
                    marginBottom: "5px",
                  }}
                >
                  {item.label === "State of Incorporation" 
                    ? `${getStateProvinceLabel(selectedCountry)} of Incorporation`
                    : item.label === "Physical Zip Code"
                    ? `Physical ${getZipPostalLabel(selectedCountry)}`
                    : item.label === "Mailing Zip Code"
                    ? `Mailing ${getZipPostalLabel(selectedCountry)}`
                    : item.label.includes("State")
                    ? item.label.replace("State", getStateProvinceLabel(selectedCountry))
                    : item.label}
                </Typography>

                {item.isDropdown ? (
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={item.options || getStatesProvinces(selectedCountry)}
                    value={
                      (item.options || getStatesProvinces(selectedCountry)).find(
                        (option) =>
                          (typeof option === "string" ? option : option.value) === item.value
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      const e = {
                        target: {
                          value: newValue
                            ? typeof newValue === "string"
                              ? newValue
                              : newValue.value
                            : "",
                        },
                      };
                      handleInputChange(index, e);
                    }}
                    onBlur={() => patchMerchant(index)}
                    onOpen={() => handleDropdownToggle(item.label)}
                    onClose={() => handleDropdownToggle(null)}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      (typeof option === "string" ? option : option.value) ===
                      (typeof value === "string" ? value : value?.value)
                    }
                    popupIcon={
                      openDropdown === item.label ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    }
                    clearIcon={null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        error={item.error}
                        helperText={item.helperText}
                        FormHelperTextProps={{ sx: { mx: 0 } }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        style={{
                          padding: "8px 12px",
                          fontSize: "14px",
                          ...props.style,
                        }}
                      >
                        {typeof option === "string" ? option : option.label}
                      </li>
                    )}
                    ListboxProps={{
                      sx: {
                        maxHeight: 250,
                        "&::-webkit-scrollbar": { width: 6 },
                        "&::-webkit-scrollbar-thumb": { backgroundColor: "#cbd5e1" },
                      },
                    }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    value={item.value || ""}
                    size="small"
                    variant="standard"
                    onChange={(event) => handleInputChange(index, event)}
                    onBlur={() => patchMerchant(index)}
                    error={item.error}
                    helperText={item.helperText}
                    FormHelperTextProps={{ sx: { mx: 0 } }}
                    placeholder={item.key === "businessStartDate" ? "YYYY-MM-DD" : ""}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </form>
      )}
      <MDSnackbar
        open={snackbarOpen}
        close={(event, reason) => {
          // Prevent auto-close behaviors - only allow manual close or your custom timeout
          if (reason === "timeout" || reason === "clickaway" || reason === "escapeKeyDown") {
            return;
          }
          setSnackbarOpen(false);
          setErrorMessage("");
          console.log("Closing snackbar manually");
        }}
        autoHideDuration={null}
        icon="notifications"
        title={"Error"}
        content={errorMessage}
        dateTime=""
      ></MDSnackbar>
    </Box>
  );
};

export default MerchantBusinessInformation;
